import { gql } from '@apollo/client';
import IconStarSolid from '@apollo/icons/default/IconStarSolid.svg';
import IconVariant from '@apollo/icons/default/IconVariant.svg';
import { ListItem } from '@apollo/space-kit/ListItem';
import { Select } from '@apollo/space-kit/Select';
import classnames from 'classnames';
import { keyBy, orderBy } from 'lodash';
import React, { useMemo } from 'react';

import { VariantSelectFragment } from 'src/app/graph/sandboxShared/VariantSelect';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export const GraphSelectFragment = gql`
  fragment GraphSelectFragment on Service {
    description
    title
    id
    variants {
      id
      isFavoriteOfCurrentUser
      name
      ...VariantSelectFragment
    }
    roles {
      canCheckSchemas
    }
  }
  ${VariantSelectFragment}
`;

export const GraphSelect = ({
  triggerText,
  selectedGraphId,
  setSelectedGraphId,
  graphs,
  disableTrigger = false,
  disabledItemChecker = () => undefined,
  onBlur,
}: {
  triggerText: string | undefined;
  selectedGraphId: string | null;
  setSelectedGraphId: (graphId: string) => void;
  graphs: GraphQLTypes.GraphSelectFragment[] | undefined;
  disableTrigger?: boolean;
  disabledItemChecker?: (
    graph: GraphQLTypes.GraphSelectFragment,
  ) => { isDisabled: boolean; disabledExplanation: string } | undefined;
  onBlur?: React.FocusEventHandler<HTMLSelectElement>;
}) => {
  const keyedGraphsWithFavorites = useMemo(
    () =>
      keyBy(
        graphs?.map((graph) => {
          return {
            ...graph,
            hasFavorite: graph.variants.some(
              (variant) => variant.isFavoriteOfCurrentUser,
            ),
          };
        }),
        (graph) => graph.id,
      ),
    [graphs],
  );

  const someGraphHasFavorites = useMemo(
    () =>
      Object.values(keyedGraphsWithFavorites).some(
        (graph) => graph.hasFavorite,
      ),
    [keyedGraphsWithFavorites],
  );

  return (
    <Select
      disabled={disableTrigger}
      feel="flat"
      size="standard"
      className="max-w-44 truncate border-none"
      data-testid="graph_select"
      listAs={<div className="max-h-72 w-72 outline-none" />}
      renderTriggerNode={() => <>{triggerText ?? 'select a graph'}</>}
      renderListItem={(itemProps, options) => {
        const graphId = options.props.value;
        const graph =
          typeof graphId === 'string' && keyedGraphsWithFavorites[graphId];
        if (!graph) {
          throw Error(`Could not find graphId: ${graphId}in the passed graphs`);
        }

        const { isDisabled, disabledExplanation } =
          disabledItemChecker(graph) ?? {};

        return (
          <ListItem
            interactive={!isDisabled}
            {...itemProps}
            key={graphId}
            className={classnames(
              'size-full rounded p-1 px-2 text-base',
              isDisabled && 'opacity-30',
            )}
          >
            <Tooltip
              label={disabledExplanation}
              disabled={!isDisabled || !disabledExplanation}
            >
              <div
                className={classnames(
                  someGraphHasFavorites && !graph.hasFavorite && 'pl-4',
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="relative flex items-center truncate">
                    {graph.hasFavorite && (
                      <IconStarSolid className="mr-1 text-icon-change" />
                    )}
                    <span className="font-semibold">{graph.title}</span>
                  </div>
                  <div className="flex items-center">
                    <IconVariant
                      className={classnames(
                        'mr-1',
                        itemProps.selected
                          ? 'text-inverted'
                          : 'text-icon-primary',
                      )}
                    />
                    <span className="mr-1 font-semibold">
                      {graph.variants.length}
                    </span>
                  </div>
                </div>
                <div
                  className={classnames(
                    'max-h-16 whitespace-pre-wrap text-left text-sm',
                    itemProps.selected ? 'text-inverted' : 'text-secondary',
                    graph.hasFavorite && 'pl-4',
                  )}
                >
                  {graph.description}
                </div>
              </div>
            </Tooltip>
          </ListItem>
        );
      }}
      value={selectedGraphId}
      onChange={(e) => {
        setSelectedGraphId(e.target.value);
      }}
      onBlur={onBlur}
    >
      {orderBy(
        Object.values(keyedGraphsWithFavorites),
        [
          (graph) => (graph && disabledItemChecker(graph)?.isDisabled ? 1 : -1),
          (graph) => (graph.hasFavorite ? -1 : 1),
          (graph) => graph.variants.length,
          (graph) => graph.title.toLowerCase(),
        ],
        ['asc', 'asc', 'desc'],
      ).map((graph) => {
        const isDisabled = graph && disabledItemChecker(graph)?.isDisabled;

        return <option value={graph.id} key={graph.id} disabled={isDisabled} />;
      })}
    </Select>
  );
};
